import dayjs from 'dayjs';
import { formatDate } from '@utils/delivery';
import {
  StoreHour,
  StoreException,
  Store,
} from '@adapters/prismic/fetching/stores/types';
import SliceHandler from '@components/cms/SliceHandler';

export const applyExceptions = (
  week: Map<string, StoreHour>,
  exceptions: StoreException[],
  today: Date = new Date()
): Map<string, StoreHour> => {
  if (exceptions?.length === 0) {
    return week;
  }

  const yesterday = dayjs(today).subtract(1, 'day');
  const endOfWeek = dayjs(today).add(6, 'day');

  const weekMap = week;
  exceptions?.forEach(({ date, open, close, exception_notice, name }) => {
    const formattedDate = dayjs(date);

    if (
      dayjs(formattedDate).isAfter(yesterday) &&
      dayjs(formattedDate).isBefore(endOfWeek)
    ) {
      const day = dayjs(formattedDate).format('dddd');
      weekMap.set(day, {
        day,
        open,
        close,
        dow_notice: exception_notice,
        name,
      });
    }
  });

  return weekMap;
};

export const generateOrderedWeek = (
  week: StoreHour[],
  today: Date = new Date()
): Map<string, StoreHour> => {
  const weekDictionary: string[] = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  const currentDayIndex = today.getDay();

  const weekMap = new Map();
  for (let i = 0; i < 7; i++) {
    const orderIndex = (i + currentDayIndex) % 7;
    weekMap.set(weekDictionary[orderIndex], null);
  }

  week?.forEach(({ day, ...rest }) => {
    weekMap.set(day, {
      day,
      ...rest,
    });
  });
  return weekMap;
};

const cleanUpToArray = (weekMap: Map<string, StoreHour>): StoreHour[] =>
  [...weekMap.values()].filter((el) => el != null);

export const weeklyHours = (
  week: StoreHour[],
  exceptions: StoreException[],
  today: Date = new Date()
): StoreHour[] => {
  const orderedWeek = generateOrderedWeek(week, today);

  const weekWithExceptions = applyExceptions(orderedWeek, exceptions, today);

  return cleanUpToArray(weekWithExceptions);
};

const CLOSED_STORE_TERMS = ['closed', 'fermé', 'close', 'ferme'] as const;

export const isClosedStoreHourValue = (value?: string | null): boolean => {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) return true;
  return CLOSED_STORE_TERMS.includes(
    normalized as (typeof CLOSED_STORE_TERMS)[number]
  );
};

export const generateStoreTabsComponents = (rawData: any) => {
  const getPanelComponents = (body: any) => (
    <>
      {body.map((slice: any, i: number) => (
        <SliceHandler
          key={`store-slice-type-${i}`}
          slice={slice}
          pageType="content"
        />
      ))}
    </>
  );

  const data = rawData.map((tab: { tabContent: any; panelContent: any }) => ({
    tabContent: tab.tabContent,
    panelContent: getPanelComponents(tab.panelContent),
  }));
  return data;
};

export const getLocalizedMonth = (
  monthIndex: number,
  locales?: string | string[],
  monthFormat: 'long' | 'short' = 'long'
): string => {
  const year = new Date().getFullYear(); // 2020
  const formatter = new Intl.DateTimeFormat(locales, {
    month: monthFormat,
  });
  const getMonthName = (index: number) =>
    formatter.format(new Date(year, index));
  return getMonthName(monthIndex);
};

export const getPickupDates = (
  favouriteStoreDetails: Store | null | undefined,
  locale = 'en'
) => {
  const today = new Date();
  const startDate = new Date(today);
  const endDate = new Date(today);

  // Helper function to check if store is closed
  const isStoreClosed = (hours: any) => {
    if (!hours?.open?.trim()) return true;
    return (
      isClosedStoreHourValue(hours?.open) ||
      isClosedStoreHourValue(hours?.close)
    );
  };

  // Get store hours with exceptions
  const storeHours = favouriteStoreDetails?.data?.store_hours;
  const storeExceptions = favouriteStoreDetails?.data?.store_hour_exceptions;
  const weeklyStoreHours = storeHours
    ? weeklyHours(storeHours, storeExceptions || [])
    : null;

  // Calculate start date (5 business days from today)
  let businessDaysAdded = 0;
  while (businessDaysAdded < 5) {
    startDate.setDate(startDate.getDate() + 1);

    // Skip weekends (Saturday = 6, Sunday = 0)
    if (startDate.getDay() === 0 || startDate.getDay() === 6) {
      continue;
    }

    // Check if store is closed on this day
    if (weeklyStoreHours) {
      const dayName = startDate.toLocaleDateString(
        locale === 'fr' ? 'fr-CA' : 'en-CA',
        {
          weekday: 'long',
        }
      );
      const storeDay = weeklyStoreHours.find((day: any) => day.day === dayName);
      if (storeDay && isStoreClosed(storeDay)) continue;
    }

    businessDaysAdded += 1;
  }

  // Calculate end date (7 business days from today)
  businessDaysAdded = 0;
  while (businessDaysAdded < 7) {
    endDate.setDate(endDate.getDate() + 1);

    // Skip weekends (Saturday = 6, Sunday = 0)
    if (endDate.getDay() === 0 || endDate.getDay() === 6) {
      continue;
    }

    // Check if store is closed on this day
    if (weeklyStoreHours) {
      const dayName = endDate.toLocaleDateString(
        locale === 'fr' ? 'fr-CA' : 'en-CA',
        {
          weekday: 'long',
        }
      );
      const storeDay = weeklyStoreHours.find((day: any) => day.day === dayName);
      if (storeDay && isStoreClosed(storeDay)) continue;
    }

    businessDaysAdded += 1;
  }

  const startFormatted = formatDate(startDate, locale);
  const endFormatted = formatDate(endDate, locale);

  return locale === 'fr'
    ? `${startFormatted} et le ${endFormatted}`
    : `${startFormatted} – ${endFormatted}`;
};
